import type { ScheduleResponse, VenueInfo } from '../shared/types.js';
import { fetchConfig, fetchReservations, fetchSchedule, fetchScheduleMeta, fetchVenues, fetchWatches, pushReservations, pushWatches } from './api.js';
import {
  hourFrom,
  hourTo,
  isVenueVisible,
  isWeekdayVisible,
  knownVenues,
  loadFromLocalStorage,
  myReservations,
  previousData,
  replaceMyReservations,
  replaceWatchedSlots,
  saveToLocalStorage,
  setKnownVenues,
  setVenueBookingUrls,
  setPreviousData,
  setSlotWatched,
  setHourRange,
  setVisibleVenues,
  setVisibleWeekdays,
  setWatcherEmail,
  toggleMyReservation,
  visibleVenues,
  visibleWeekdays,
  watchedSlots,
  watcherEmail
} from './state.js';
import {
  availableDates,
  focusSlot,
  groupByDate,
  isSingleDayMode,
  renderData,
  renderDayFilter,
  renderDayNav,
  renderVenueFilter,
  setDayNavSelection,
  renderWatchList,
  SINGLE_DAY_QUERY,
  WEEKDAYS
} from './render.js';
import { initDragSelect } from './dragSelect.js';
import { parseSlotKey } from '../shared/slotKey.js';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// How often to ask the server whether its snapshot changed. Server-owned
// (see /api/config); this is only the fallback until — or if — that first
// config fetch fails.
let POLL_INTERVAL = 5_000;
// Even a near-instant fetch keeps the spinner up this long, so the user can
// actually see that a refresh happened instead of a sub-frame flicker.
const MIN_SPINNER_MS = 200;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let isLoading = false;
let venues: VenueInfo[] = [];

const el = {
  loadingSpinner: document.getElementById('loadingSpinner') as HTMLElement,
  lastUpdate: document.getElementById('lastUpdate') as HTMLElement,
  systemWarning: document.getElementById('systemWarning') as HTMLElement,
  venueFilter: document.getElementById('venueFilter') as HTMLElement,
  dayFilter: document.getElementById('dayFilter') as HTMLElement,
  dayNav: document.getElementById('dayNav') as HTMLElement,
  hourFromSelect: document.getElementById('hourFrom') as HTMLSelectElement,
  hourToSelect: document.getElementById('hourTo') as HTMLSelectElement,
  emailInput: document.getElementById('emailInput') as HTMLInputElement,
  emailDialog: document.getElementById('emailDialog') as HTMLDialogElement,
  emailDialogForm: document.getElementById('emailDialogForm') as HTMLFormElement,
  emailDialogInput: document.getElementById('emailDialogInput') as HTMLInputElement,
  emailDialogCancel: document.getElementById('emailDialogCancel') as HTMLButtonElement,
  priceListDialog: document.getElementById('priceListDialog') as HTMLDialogElement,
  priceListTitle: document.getElementById('priceListTitle') as HTMLElement,
  priceListImage: document.getElementById('priceListImage') as HTMLImageElement,
  priceListLink: document.getElementById('priceListLink') as HTMLAnchorElement,
  priceListClose: document.getElementById('priceListClose') as HTMLButtonElement,
  watchStatus: document.getElementById('watchStatus') as HTMLElement,
  unwatchAllButton: document.getElementById('unwatchAllButton') as HTMLButtonElement,
  sidebar: document.querySelector('.sidebar') as HTMLElement,
  sidebarToggle: document.getElementById('sidebarToggle') as HTMLButtonElement,
  watchList: document.getElementById('watchList') as HTMLElement,
  mainContent: document.querySelector('.main-content') as HTMLElement,
  content: document.getElementById('content') as HTMLElement,
  error: document.getElementById('error') as HTMLElement
};

// Registers the current full watched-slot set with the server under
// whatever email is currently entered, so the background watcher can email
// freed slots even with no browser open. A no-op (not an error) with no
// valid-looking email entered yet — watching just stays local-only, same as
// before this feature existed.
function syncWatchesToServer(): void {
  if (!watcherEmail || !EMAIL_PATTERN.test(watcherEmail)) return;
  pushWatches(watcherEmail, [...watchedSlots])
    .then(({ slots }) => {
      // The server may have dropped some (free/elapsed slots can't be newly
      // watched) — mirror what it kept so the highlights tell the truth.
      const same = slots.length === watchedSlots.size && slots.every((key) => watchedSlots.has(key));
      if (same) return;
      replaceWatchedSlots(slots);
      renderCurrentData();
    })
    .catch((error) => {
      console.error('Nepodařilo se synchronizovat sledované sloty se serverem:', error);
    });
}

// Watches on slots whose time has gone by are useless — the server prunes
// them every scrape; this keeps the local mirror from showing "uplynulo"
// rows until the next reload.
function pruneExpiredLocally(data: ScheduleResponse): void {
  let changed = false;
  watchedSlots.forEach((key) => {
    const { venueId, date, courtId, time } = parseSlotKey(key);
    const slot = data.venues.find((v) => v.id === venueId)?.days.find((d) => d.date.dateString === date)?.courts.find((c) => c.id === courtId)?.slots[time];
    if (slot?.isPast) {
      watchedSlots.delete(key);
      changed = true;
    }
  });
  if (changed) saveToLocalStorage();
}

// A "my reservation" mark lives on a booked slot — or on a still-available
// one (marked before the booking system caught up, or reserved outside it).
// Only a slot that reads "closed" clearly can't be anyone's reservation, so
// that's the only status that drops the mark; available→booked is the
// expected confirmation of an early mark, never a reason to clear it.
function pruneStaleReservationsLocally(data: ScheduleResponse): void {
  let changed = false;
  myReservations.forEach((key) => {
    const { venueId, date, courtId, time } = parseSlotKey(key);
    const slot = data.venues.find((v) => v.id === venueId)?.days.find((d) => d.date.dateString === date)?.courts.find((c) => c.id === courtId)?.slots[time];
    if (slot && slot.status === 'closed') {
      myReservations.delete(key);
      changed = true;
    }
  });
  if (changed) {
    saveToLocalStorage();
    // Push the pruned set, otherwise the server copy would resurrect the
    // dropped marks on the next restore.
    syncReservationsToServer();
  }
}

// The server copy is the source of truth for an email's watches (it's what
// the emailer acts on, and an unsubscribe link clears it there) — the
// browser just mirrors it so the highlights survive a reload on any device.
async function restoreWatchesFromServer(): Promise<void> {
  if (!watcherEmail) return;
  try {
    const { slots } = await fetchWatches(watcherEmail);
    replaceWatchedSlots(slots);
  } catch (error) {
    console.error('Nepodařilo se načíst sledované termíny ze serveru:', error);
  }
}

// The "my reservation" marks follow the same mirror-to-server pattern as
// watches (per email, wholesale replace) — browser storage alone kept
// losing them (iOS evicts localStorage after ~7 days away) and couldn't
// share them across devices. With no valid email set they stay local-only,
// exactly like watches picked before an email existed.
function syncReservationsToServer(): void {
  if (!watcherEmail || !EMAIL_PATTERN.test(watcherEmail)) return;
  pushReservations(watcherEmail, [...myReservations]).catch((error) => {
    console.error('Nepodařilo se synchronizovat rezervace se serverem:', error);
  });
}

async function restoreReservationsFromServer(): Promise<void> {
  if (!watcherEmail) return;
  try {
    const { slots } = await fetchReservations(watcherEmail);
    replaceMyReservations(slots);
  } catch (error) {
    console.error('Nepodařilo se načíst rezervace ze serveru:', error);
  }
}

// Shared by both entry points (the sidebar field and the dialog below) —
// whichever one the address came from, the other stays in sync, and
// whatever it should trigger (push newly-local watches, or pull this
// address's existing ones) happens the same way either way.
async function applyEmail(value: string): Promise<void> {
  el.emailInput.value = value;
  const hadEmail = watcherEmail !== null;
  setWatcherEmail(value);

  if (!hadEmail && watchedSlots.size > 0) {
    // Slots picked before this feature required an email (older
    // localStorage) belong to the first email entered.
    syncWatchesToServer();
  } else {
    // Switching to another address shows that address's own watches.
    await restoreWatchesFromServer();
  }

  // Reservation marks mirror the same first-email-claims-local-state rule.
  if (!hadEmail && myReservations.size > 0) {
    syncReservationsToServer();
  } else {
    await restoreReservationsFromServer();
  }
  renderCurrentData();
}

// Watches are stored server-side under an email, so one has to exist
// before the first slot can be picked — otherwise there'd be nothing to
// attach the selection to (and nobody to notify). Opens the dialog rather
// than blocking with alert(); the user just repeats their tap/drag once
// it's set.
function ensureEmail(): boolean {
  if (watcherEmail && EMAIL_PATTERN.test(watcherEmail)) return true;
  openEmailDialog();
  return false;
}

function openEmailDialog(): void {
  if (el.emailDialog.open) return;
  el.emailDialogInput.value = watcherEmail ?? el.emailInput.value.trim();
  el.emailDialog.showModal();
  el.emailDialogInput.focus();
}

function pluralTerminy(count: number): string {
  if (count === 1) return '1 termín';
  if (count >= 2 && count <= 4) return `${count} termíny`;
  return `${count} termínů`;
}

// One line under the section header; the empty list itself already says
// "nothing yet, click the schedule", so with an email and no watches this
// stays blank (the .hint:empty rule collapses it).
function updateWatchStatus(): void {
  if (!watcherEmail) {
    el.watchStatus.textContent = 'Nejdřív zadej email — sledované termíny se k němu ukládají.';
  } else if (watchedSlots.size === 0) {
    el.watchStatus.textContent = '';
  } else {
    el.watchStatus.textContent = `Sledujete ${pluralTerminy(watchedSlots.size)}. Uvolněné jsou zvýrazněné, kliknutím je najdeš v rozvrhu.`;
  }
}

// Which day the mobile layout is showing (see render.ts's isSingleDayMode).
// Deliberately not persisted: a stored date goes stale overnight, and
// "whatever is soonest" is the right default every time the app opens.
let selectedDate: string | null = null;
// The day the picker was last auto-scrolled to. renderDayNav leaves the
// strip's DOM (and so its scroll position) alone when nothing about it
// changed, so re-centering only on a moved selection doesn't fight a user
// who had scrolled it themselves.
let dayNavScrolledFor: string | null = null;
// The visible dates the last render was built from, so the arrows can step
// through them without regrouping the whole snapshot on every tap.
let visibleDates: string[] = [];

function selectDate(dateString: string): void {
  if (dateString === selectedDate) return;
  selectedDate = dateString;
  renderCurrentData();
}

function stepDate(step: number): void {
  const index = selectedDate ? visibleDates.indexOf(selectedDate) : -1;
  const next = visibleDates[index + step];
  if (next) selectDate(next);
}

// ---------- Desktop day picker ----------
// Desktop shows every day at once, so the same picker navigates instead of
// switching: a chip scrolls that day's card to the top, and the highlighted
// chip follows whichever day is currently being read.

// The day whose card is under the picker right now, per the observer below.
let dayInView: string | null = null;
let dayObserver: IntersectionObserver | null = null;
const daysInBand = new Set<string>();

function scrollToDay(dateString: string): void {
  const section = el.content.querySelector<HTMLElement>(`.day-section[data-date="${CSS.escape(dateString)}"]`);
  if (!section) return;
  // scrollIntoView rather than arithmetic on getBoundingClientRect: with
  // content-visibility the cards in between are only laid out as the scroll
  // reaches them, so a computed offset would be measured against placeholder
  // heights. The browser lays the target itself out first, and the second
  // pass corrects for any cards that resized on the way there.
  section.scrollIntoView({ block: 'start' });
  requestAnimationFrame(() => section.scrollIntoView({ block: 'start' }));
}

// Keeps the highlighted chip reachable without yanking the strip around:
// only scrolls when the chip has actually gone out of view.
function revealSelectedChip(center: boolean): void {
  const strip = el.dayNav.querySelector<HTMLElement>('.day-nav-strip');
  const chip = strip?.querySelector<HTMLElement>('.day-chip.selected');
  if (!strip || !chip) return;
  // scrollLeft directly rather than scrollIntoView, which would also scroll
  // the page vertically to bring the strip into view.
  if (center) {
    strip.scrollLeft = chip.offsetLeft - (strip.clientWidth - chip.offsetWidth) / 2;
  } else if (chip.offsetLeft < strip.scrollLeft) {
    strip.scrollLeft = chip.offsetLeft - 8;
  } else if (chip.offsetLeft + chip.offsetWidth > strip.scrollLeft + strip.clientWidth) {
    strip.scrollLeft = chip.offsetLeft + chip.offsetWidth - strip.clientWidth + 8;
  }
}

// Watches a thin band just under the picker and reports which day's card
// occupies it. An observer rather than a scroll handler: this fires only on
// the boundaries being crossed, instead of measuring 21 cards every frame.
function observeDaySections(): void {
  if (typeof IntersectionObserver === 'undefined') return;
  dayObserver?.disconnect();
  daysInBand.clear();
  dayObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const date = (entry.target as HTMLElement).dataset.date;
        if (!date) return;
        if (entry.isIntersecting) {
          daysInBand.add(date);
        } else {
          daysInBand.delete(date);
        }
      });
      // Several cards can share the band on a tall window; the earliest is
      // the one whose heading is nearest the top.
      const next = [...daysInBand].sort()[0] ?? null;
      if (next === dayInView) return;
      dayInView = next;
      setDayNavSelection(el.dayNav, visibleDates, dayInView);
      revealSelectedChip(false);
    },
    { rootMargin: '-72px 0px -80% 0px' }
  );
  el.content.querySelectorAll('.day-section[data-date]').forEach((section) => dayObserver!.observe(section));
}

function renderCurrentData(): void {
  updateWatchStatus();
  renderWatchList(el.watchList, previousData);
  if (!previousData) return;

  // Grouped once here and passed on: both the picker and the schedule need
  // the same filtered view, and building it walks every venue-day.
  const byDate = groupByDate(previousData);
  visibleDates = availableDates(byDate);
  const singleDay = isSingleDayMode();
  // A selection can fall out of the list when a day elapses or a filter
  // changes — fall back to the soonest day still shown.
  if (selectedDate === null || !visibleDates.includes(selectedDate)) {
    selectedDate = visibleDates[0] ?? null;
  }
  // Both layouts get the same strip of days; what a chip does differs.
  // Mobile switches to that day (it's the only one rendered), desktop
  // scrolls to it.
  renderDayNav(el.dayNav, visibleDates, new Date());
  if (singleDay) {
    setDayNavSelection(el.dayNav, visibleDates, selectedDate);
    if (selectedDate !== dayNavScrolledFor) {
      dayNavScrolledFor = selectedDate;
      revealSelectedChip(true);
    }
  } else {
    // Coming back to mobile should re-center, since the selection there is
    // a choice rather than a reflection of where the page is scrolled.
    dayNavScrolledFor = null;
    setDayNavSelection(el.dayNav, visibleDates, dayInView);
  }
  // The whole day-table is rebuilt via innerHTML on every refresh — without
  // this, an in-progress auto-refresh would silently reset whatever day the
  // user had scrolled down to back to the top on every auto-refresh. The
  // page now scrolls at window level (desktop and mobile alike), so it's
  // the window position that matters; the element restore stays as a cheap
  // safety net should .main-content ever get its own scroll pane back.
  const scrollTop = el.mainContent.scrollTop;
  const pageScroll = window.scrollY;
  renderData(el.content, previousData, byDate, visibleDates, singleDay ? selectedDate : null);
  el.mainContent.scrollTop = scrollTop;
  window.scrollTo(0, pageScroll);

  // Sections are only replaced wholesale when the set of days changes, but
  // when that happens the observer is left watching detached elements.
  if (singleDay) {
    dayObserver?.disconnect();
    dayObserver = null;
  } else {
    observeDaySections();
  }
}

function renderFilter(): void {
  renderVenueFilter(
    el.venueFilter,
    venues,
    (id) => isVenueVisible(id),
    (venueId, checked) => {
      const next = new Set(visibleVenues ?? venues.map((v) => v.id));
      if (checked) {
        next.add(venueId);
      } else {
        next.delete(venueId);
      }
      setVisibleVenues(next);
      renderCurrentData();
    }
  );
}

// Day-of-week filter (e.g. "only Tuesdays and Sundays") — same pattern as
// the venue filter, but the seven options are fixed, so there's no server
// fetch and no new-item bookkeeping.
function renderDayFilterUI(): void {
  renderDayFilter(
    el.dayFilter,
    (weekday) => isWeekdayVisible(weekday),
    (weekday, checked) => {
      const next = new Set(visibleWeekdays ?? WEEKDAYS.map((w) => w.value));
      if (checked) {
        next.add(weekday);
      } else {
        next.delete(weekday);
      }
      setVisibleWeekdays(next);
      renderCurrentData();
    }
  );
}

// Hour-range filter ("only interested from 17:00 to 22:00") — the schedule
// renders exactly these columns, every day-table the same (see
// renderScheduleTable). `to` is the end of play, exclusive of its own
// hour's slots: picking 22:00 shows columns up to 21:30.
function syncHourSelects(): void {
  el.hourFromSelect.value = String(hourFrom);
  el.hourToSelect.value = String(hourTo);
}

function initHourFilter(): void {
  for (let h = 6; h <= 22; h++) {
    el.hourFromSelect.add(new Option(`${h}:00`, String(h)));
  }
  for (let h = 7; h <= 23; h++) {
    el.hourToSelect.add(new Option(`${h}:00`, String(h)));
  }
  syncHourSelects();

  const onChange = (changed: HTMLSelectElement) => {
    let from = Number(el.hourFromSelect.value);
    let to = Number(el.hourToSelect.value);
    // An inverted pick drags the other end along instead of erroring; both
    // results stay inside each select's own option range (from ≤ 22 keeps
    // to = from+1 ≤ 23, to ≥ 7 keeps from = to−1 ≥ 6).
    if (from >= to) {
      if (changed === el.hourFromSelect) {
        to = from + 1;
      } else {
        from = to - 1;
      }
    }
    setHourRange(from, to);
    syncHourSelects();
    renderCurrentData();
  };
  el.hourFromSelect.addEventListener('change', () => onChange(el.hourFromSelect));
  el.hourToSelect.addEventListener('change', () => onChange(el.hourToSelect));
}

async function initVenueFilter(): Promise<void> {
  venues = await fetchVenues();
  setVenueBookingUrls(new Map(venues.filter((v) => v.bookingUrl).map((v) => [v.id, v.bookingUrl!])));
  const currentIds = venues.map((v) => v.id);

  if (visibleVenues === null) {
    // First visit ever (nothing stored yet): everything starts on.
    setVisibleVenues(new Set(currentIds));
  } else {
    // A venue id that's new since the last visit (knownVenues doesn't have
    // it) defaults to visible rather than silently vanishing from the
    // filter — visibleVenues alone can't tell "just appeared" apart from
    // "user unchecked it", both look like "missing from the stored set".
    // The very first run after this fix ships has no knownVenues at all
    // (nothing was ever tracked before), so every current venue counts as
    // "new" that one time — re-showing anything a user had actually
    // unchecked earlier, but only once; from then on knownVenues is
    // accurate and only genuinely new venues get this treatment.
    const newIds = currentIds.filter((id) => !(knownVenues?.has(id) ?? false));
    if (newIds.length > 0) {
      setVisibleVenues(new Set([...visibleVenues, ...newIds]));
    }
  }

  setKnownVenues(new Set(currentIds));
  renderFilter();
}

// Jumps to a watched slot's cell in the schedule. If its venue is filtered
// out there's no cell to jump to, so the filter is switched back on first.
function showSlotInSchedule(key: string): void {
  const { venueId, date } = parseSlotKey(key);
  if (!isVenueVisible(venueId)) {
    setVisibleVenues(new Set([...(visibleVenues ?? venues.map((v) => v.id)), venueId]));
    renderFilter();
  }
  // Its weekday may be filtered out too (same "no cell to jump to" problem
  // as a hidden venue) — switch that day back on the same way.
  const weekday = new Date(`${date}T00:00:00`).getDay();
  if (!isWeekdayVisible(weekday)) {
    setVisibleWeekdays(new Set([...(visibleWeekdays ?? WEEKDAYS.map((w) => w.value)), weekday]));
    renderDayFilterUI();
  }
  // On mobile only one day is rendered at a time, so the slot's own day has
  // to be the selected one before there's any cell to scroll to. Every
  // adjustment above is applied to the state first, so one render covers
  // all of them rather than rebuilding the schedule up to three times.
  if (isSingleDayMode()) selectedDate = date;
  renderCurrentData();
  if (!focusSlot(el.content, key)) {
    alert('Tento termín teď v rozvrhu není — jeho den se nepodařilo načíst, nebo už je mimo načtený rozsah.');
  }
}

// Shows the snapshot's own server-side timestamp (local time) rather than
// "when this tab received it", so it stays truthful across reloads that
// restore cached data without re-downloading.
function showLastUpdate(fetchedAt: string): void {
  el.lastUpdate.textContent = `Aktualizováno ${new Date(fetchedAt).toLocaleTimeString('cs-CZ')}`;
}

async function fetchData(): Promise<void> {
  if (isLoading) return;

  isLoading = true;
  const startedAt = Date.now();
  el.loadingSpinner.hidden = false;
  el.error.innerHTML = '';

  try {
    const data: ScheduleResponse = await fetchSchedule();
    setPreviousData(data);
    pruneExpiredLocally(data);
    pruneStaleReservationsLocally(data);
    renderCurrentData();
    saveToLocalStorage();
    showLastUpdate(data.fetchedAt);
  } catch (error) {
    console.error('Kritická chyba při načítání:', error);
    el.error.innerHTML = `
      <div class="error-message">
          <h2>❌ Chyba při načítání dat</h2>
          <p><strong>${(error as Error).message}</strong></p>
      </div>
    `;
  } finally {
    const remaining = MIN_SPINNER_MS - (Date.now() - startedAt);
    setTimeout(() => {
      el.loadingSpinner.hidden = true;
      isLoading = false;
    }, Math.max(0, remaining));
  }
}

// The cheap 5s poll: only the snapshot timestamp. The full dataset is
// downloaded only when the server has actually re-scraped since the data
// we're showing (which persists across reloads via localStorage, so a
// reload doesn't re-download an unchanged snapshot either).
async function checkForUpdate(): Promise<void> {
  if (isLoading) return;
  // A backgrounded tab has nothing to show and no reason to download or
  // re-render a ~2 MB snapshot; iOS in particular throttles such a tab and
  // then runs the backlog at once when it returns, which read as a freeze.
  // The visibilitychange handler catches up on the way back in.
  if (document.hidden) return;
  try {
    const meta = await fetchScheduleMeta();
    if (meta.fetchedAt && meta.fetchedAt !== previousData?.fetchedAt) {
      await fetchData();
    }
  } catch (error) {
    console.error('Nepodařilo se ověřit aktuálnost dat:', error);
  }
}

function unwatchAll(): void {
  const count = watchedSlots.size;
  if (count === 0) {
    alert('Nejsou žádné sledované sloty!');
    return;
  }

  if (confirm(`Opravdu chcete zrušit sledování všech ${count} slotů?`)) {
    watchedSlots.clear();
    saveToLocalStorage();
    renderCurrentData();
    syncWatchesToServer();
    alert('Všechna sledování byla zrušena!');
  }
}

function startPolling(): void {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = setInterval(checkForUpdate, POLL_INTERVAL);
}

// The mobile day picker — a chip jumps straight to that day, the arrows
// step one day either way. Delegated, since the strip is re-rendered as
// innerHTML on every refresh.
el.dayNav.addEventListener('click', (event) => {
  const target = event.target as HTMLElement;
  const singleDay = isSingleDayMode();

  const chip = target.closest<HTMLElement>('.day-chip');
  if (chip?.dataset.date) {
    if (singleDay) {
      selectDate(chip.dataset.date);
    } else {
      scrollToDay(chip.dataset.date);
    }
    return;
  }

  const arrow = target.closest<HTMLElement>('.day-nav-arrow');
  if (!arrow?.dataset.step) return;
  const step = Number(arrow.dataset.step);
  if (singleDay) {
    stepDate(step);
    return;
  }
  // Desktop steps relative to the day being read, not to a stored choice.
  const from = dayInView ?? visibleDates[0];
  const next = visibleDates[visibleDates.indexOf(from) + step];
  if (next) scrollToDay(next);
});

// Crossing the mobile/desktop breakpoint switches between "one day at a
// time" and "every day listed", so the schedule has to be rebuilt.
// addEventListener on a MediaQueryList is missing on older iOS, hence the
// deprecated addListener fallback — this is exactly the device that needs it.
const onBreakpointChange = (): void => renderCurrentData();
if (SINGLE_DAY_QUERY.addEventListener) {
  SINGLE_DAY_QUERY.addEventListener('change', onBreakpointChange);
} else {
  (SINGLE_DAY_QUERY as MediaQueryList & { addListener(cb: () => void): void }).addListener(onBreakpointChange);
}

el.unwatchAllButton.addEventListener('click', unwatchAll);

// Mobile-only: filters/email/watch list start collapsed behind this toggle
// (see the .sidebar-panels media query) so the schedule is visible first.
el.sidebarToggle.addEventListener('click', () => {
  const expanded = el.sidebar.classList.toggle('expanded');
  el.sidebarToggle.setAttribute('aria-expanded', String(expanded));
});

// The watch list is re-rendered via innerHTML, so its clicks are delegated:
// ✕ unwatches that one slot, anywhere else on the row jumps to its cell.
el.watchList.addEventListener('click', (event) => {
  const target = event.target as HTMLElement;
  const item = target.closest<HTMLElement>('.watch-item');
  const key = item?.dataset.key;
  if (!key) return;

  if (target.closest('[data-action="remove"]')) {
    const { venueId, date, courtId, time } = parseSlotKey(key);
    setSlotWatched(venueId, date, courtId, time, false);
    saveToLocalStorage();
    renderCurrentData();
    syncWatchesToServer();
    return;
  }

  showSlotInSchedule(key);
});

// The "my reservation" corner button (any non-closed slot) — purely
// visual, deliberately separate from watching: works without an email
// (staying local until one is set), and once an email exists the set is
// mirrored to the server per address (see syncReservationsToServer).
// dragSelect.ts's mousedown handler already ignores
// this button so it never starts a watch drag; on touch the equivalent
// gesture is a long-press that doesn't turn into a drag (handled inside
// dragSelect.ts itself, via the same onToggleReservation callback below).
function onReservationMarkClick(button: HTMLElement): void {
  const td = button.closest<HTMLElement>('.slot-td');
  const { venue, date, court, time } = td?.dataset ?? {};
  if (!venue || !date || !court || !time) return;
  toggleMyReservation(venue, date, court, time);
  syncReservationsToServer();
  renderCurrentData();
}

// Opens a venue's price list, for the venues that publish one only as a
// picture (see render.ts's price row). The <img> is left without a src
// until now on purpose: these are ~250 kB photos and the button repeats for
// every rendered day, so eager images would be megabytes fetched for a
// dialog most visits never open.
function openPriceList(url: string, title: string): void {
  el.priceListTitle.textContent = title || 'Ceník';
  el.priceListLink.href = url;
  if (el.priceListImage.getAttribute('src') !== url) el.priceListImage.src = url;
  el.priceListDialog.showModal();
}

el.content.addEventListener('click', (event) => {
  const target = event.target as HTMLElement;

  const priceList = target.closest<HTMLElement>('.price-list-btn');
  if (priceList?.dataset.pricelistUrl) {
    openPriceList(priceList.dataset.pricelistUrl, priceList.dataset.pricelistTitle ?? '');
    return;
  }

  const button = target.closest<HTMLElement>('.reservation-mark');
  if (button) onReservationMarkClick(button);
});

el.priceListClose.addEventListener('click', () => el.priceListDialog.close());

// Clicking the backdrop (the <dialog> itself, outside its content) closes
// it, same as the email dialog. Escape is handled natively.
el.priceListDialog.addEventListener('click', (event) => {
  if (event.target === el.priceListDialog) el.priceListDialog.close();
});

// Click (or click-and-drag across many cells, even across courts/venues) to
// watch/unwatch slots. Cells are re-rendered via innerHTML on every refresh,
// but dragSelect delegates from the container so it keeps working regardless.
initDragSelect(
  el.content,
  () => {
    renderCurrentData();
    syncWatchesToServer();
  },
  ensureEmail,
  (venue, date, court, time) => {
    toggleMyReservation(venue, date, court, time);
    syncReservationsToServer();
    renderCurrentData();
  }
);

el.emailInput.addEventListener('change', async () => {
  const value = el.emailInput.value.trim();
  if (!value) return;
  if (!EMAIL_PATTERN.test(value)) {
    alert('Zadejte platnou emailovou adresu.');
    return;
  }
  await applyEmail(value);
});

el.emailDialogForm.addEventListener('submit', async (event) => {
  event.preventDefault(); // validate before letting the dialog close
  const value = el.emailDialogInput.value.trim();
  if (!EMAIL_PATTERN.test(value)) {
    el.emailDialogInput.reportValidity();
    return;
  }
  await applyEmail(value);
  el.emailDialog.close();
});

el.emailDialogCancel.addEventListener('click', () => el.emailDialog.close());

// Click on the backdrop (the <dialog> element itself, outside the <form>)
// closes it, same as Cancel — the native `cancel` event (fired on Escape)
// already closes it without any handler needed here.
el.emailDialog.addEventListener('click', (event) => {
  if (event.target === el.emailDialog) el.emailDialog.close();
});

window.addEventListener('beforeunload', () => {
  if (pollTimer) clearInterval(pollTimer);
});

// Coming back to a tab that's been away: check for a newer snapshot right
// then, rather than waiting out the poll interval (which did no work while
// hidden — see checkForUpdate).
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) void checkForUpdate();
});

window.addEventListener('DOMContentLoaded', async () => {
  const hasData = loadFromLocalStorage();

  if (watcherEmail) {
    el.emailInput.value = watcherEmail;
  }

  // A rendering bug (or, in principle, a cached blob that slipped past
  // loadFromLocalStorage's own shape check) must never stop the rest of
  // startup — fetching fresh data and populating the venue filter — from
  // running, otherwise the whole app is stuck blank.
  try {
    if (hasData) {
      renderCurrentData();
      showLastUpdate(previousData!.fetchedAt);
    } else {
      el.content.innerHTML =
        '<div style="text-align:center;padding:40px;background:white;border-radius:15px;"><h2>🏐 Kurty Praha</h2><p style="margin-top:15px;color:#6b7280;">Načítám data ze serveru…</p></div>';
    }
  } catch (error) {
    console.error('Chyba při vykreslování uložených dat, pokračuji s čerstvým načtením:', error);
  }

  // Poll cadence is server-owned; a failed config fetch just leaves the
  // built-in fallback interval in place rather than blocking startup.
  try {
    const config = await fetchConfig();
    POLL_INTERVAL = config.pollIntervalMs;
    // Watching still works without a mail key, but nobody would ever be
    // notified — say so up front rather than letting people wait for mail.
    el.systemWarning.hidden = config.mailerConfigured;
  } catch (error) {
    console.error('Nepodařilo se načíst konfiguraci ze serveru, používám výchozí interval:', error);
  }

  renderDayFilterUI();
  initHourFilter();
  await initVenueFilter();
  await restoreWatchesFromServer();
  await restoreReservationsFromServer();
  renderCurrentData();

  await checkForUpdate();
  startPolling();
});
